import React, { useState, useEffect, useRef } from 'react';
import { GeografiaService, Ciudad } from '@/core/application/directory/GeografiaService';
import { MapPin, X, Search, Navigation } from 'lucide-react';

interface CityPickerModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectCity: (ciudad: Ciudad | null) => void; // null significa "Usar GPS"
}

export const CityPickerModal = ({ visible, onClose, onSelectCity }: CityPickerModalProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Ciudad[]>([]);
  const [topCiudades, setTopCiudades] = useState<Ciudad[]>([]);
  const [loading, setLoading] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();
      if (
        e.clientX < rect.left || e.clientX > rect.right ||
        e.clientY < rect.top  || e.clientY > rect.bottom
      ) {
        onClose();
      }
    };

    if (visible) {
      dialog.showModal();
      setQuery('');
      loadTopCiudades();
      dialog.addEventListener('click', handleNativeClick);
    } else {
      dialog.close();
      dialog.removeEventListener('click', handleNativeClick);
    }

    return () => {
      dialog.removeEventListener('click', handleNativeClick);
    };
  }, [visible]);

  useEffect(() => {
    if (query.length >= 2) {
      const delayDebounceFn = setTimeout(() => {
        searchCities(query);
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setResults([]);
    }
  }, [query]);

  const loadTopCiudades = async () => {
    setLoading(true);
    const top = await GeografiaService.getTopCiudades();
    setTopCiudades(top);
    setLoading(false);
  };

  const searchCities = async (searchTerm: string) => {
    setLoading(true);
    const found = await GeografiaService.searchCiudades(searchTerm);
    setResults(found);
    setLoading(false);
  };



  const renderItem = (item: Ciudad) => (
    <button 
      key={item.ciudad_id}
      onClick={() => {
        onSelectCity(item);
        onClose();
      }}
      style={{
        display: 'flex', alignItems: 'center', padding: '12px 16px',
        borderBottom: '1px solid #F8FAFC', background: 'none', border: 'none',
        borderBottomWidth: '1px', cursor: 'pointer', width: '100%', textAlign: 'left',
        transition: 'background-color 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <div style={{ width: '40px', height: '40px', borderRadius: '20px', backgroundColor: '#F1F5F9', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '12px' }}>
        <MapPin size={20} color="#64748B" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1E293B', marginBottom: '2px' }}>{item.ciudad_nombre}</div>
        {item.estado_nombre && (
          <div style={{ fontSize: '13px', color: '#64748B' }}>{item.estado_nombre}</div>
        )}
      </div>
    </button>
  );

  return (
    <>
      <dialog
        ref={dialogRef}
        style={{
          padding: 0, border: 'none', borderRadius: '16px', backgroundColor: '#FFFFFF',
          width: '100%', maxWidth: '500px', height: '80vh', maxHeight: '800px',
          display: visible ? 'flex' : 'none', flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #F1F5F9' }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#0F172A" />
          </button>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>¿Dónde estás buscando?</span>
          <div style={{ width: '32px' }} /> {/* Spacer */}
        </div>

        <div style={{ padding: '16px', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '0 12px' }}>
            <Search size={20} color="#64748B" style={{ marginRight: '8px' }} />
            <input
              type="text"
              placeholder="Escribe tu ciudad..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              style={{ flex: 1, height: '48px', border: 'none', background: 'transparent', outline: 'none', fontSize: '16px', color: '#0F172A' }}
            />
            {loading && <div style={{ width: '16px', height: '16px', border: '2px solid #38BDF8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />}
          </div>
        </div>

        <button 
          onClick={() => {
            onSelectCity(null);
            onClose();
          }}
          style={{
            display: 'flex', alignItems: 'center', padding: '16px',
            borderBottom: '4px solid #F8FAFC', background: 'none', border: 'none',
            borderBottomWidth: '4px', cursor: 'pointer', width: '100%', textAlign: 'left'
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '20px', backgroundColor: '#E0F2FE', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '12px' }}>
            <Navigation size={20} color="#0284C7" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#0284C7', marginBottom: '2px' }}>Usar mi ubicación actual</div>
            <div style={{ fontSize: '13px', color: '#64748B' }}>Utiliza el GPS de tu dispositivo</div>
          </div>
        </button>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {(() => {
            if (query.length >= 2) {
              if (results.length > 0) return results.map(renderItem);
              if (!loading) return <p style={{ textAlign: 'center', marginTop: '24px', color: '#64748B', fontSize: '15px' }}>No encontramos ciudades con ese nombre.</p>;
              return null;
            }
            return (
              <>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>
                  Sugerencias Populares
                </div>
                {topCiudades.map(renderItem)}
              </>
            );
          })()}
        </div>
      </dialog>

      <style>{`
        dialog::backdrop {
          background: rgba(15, 23, 42, 0.4);
        }
        @keyframes spin {
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};
